import React from 'react';
import { GraduationCap } from 'lucide-react';

// Brand marks used only to identify a platform or credential issuer.
// GitHub, Google, Anthropic, Coursera and Udemy paths come from Simple Icons (CC0).
// LinkedIn reuses the mark already on the site; Microsoft is the four-square mark.

export interface BrandIconProps {
  size?: number;
  className?: string;
}

const svgProps = (size: number, className?: string) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  className,
  'aria-hidden': true as const,
  focusable: false as const,
});

const pathIcon = (d: string) => {
  const Icon: React.FC<BrandIconProps> = ({ size = 20, className }) => (
    <svg {...svgProps(size, className)}>
      <path d={d} />
    </svg>
  );
  return Icon;
};

export const LinkedInIcon = pathIcon('M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z');
export const GitHubIcon = pathIcon('M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12');
export const GoogleIcon = pathIcon('M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z');
export const AnthropicIcon = pathIcon('M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z');
export const CourseraIcon = pathIcon('M11.374 23.977c-4.183-.21-8.006-2.626-9.959-6.347-2.097-3.858-1.871-8.864.732-12.454C4.748 1.338 9.497-.698 14.281.23c4.583.857 8.351 4.494 9.358 8.911 1.122 4.344-.423 9.173-3.925 12.04-2.289 1.953-5.295 2.956-8.34 2.797zm7.705-8.05a588.737 588.737 0 0 0-3.171-1.887c-.903 1.483-2.885 2.248-4.57 1.665-2.024-.639-3.394-2.987-2.488-5.134.801-2.009 2.79-2.707 4.357-2.464a4.19 4.19 0 0 1 2.623 1.669c1.077-.631 2.128-1.218 3.173-1.855-2.03-3.118-6.151-4.294-9.656-2.754-3.13 1.423-4.89 4.68-4.388 7.919.54 3.598 3.73 6.486 7.716 6.404a7.664 7.664 0 0 0 6.404-3.563z');
export const UdemyIcon = pathIcon('M12 0L5.81 3.573v3.574l6.189-3.574 6.191 3.574V3.573zM5.81 10.148v8.144c0 1.85.589 3.243 1.741 4.234S10.177 24 11.973 24s3.269-.482 4.448-1.474c1.179-.991 1.768-2.439 1.768-4.314v-8.064h-3.242v7.85c0 2.036-1.509 3.055-2.948 3.055-1.428 0-2.947-.991-2.947-3.027v-7.878z');

export const MicrosoftIcon: React.FC<BrandIconProps> = ({ size = 20, className }) => (
  <svg {...svgProps(size, className)}>
    <rect x="1" y="1" width="10.5" height="10.5" />
    <rect x="12.5" y="1" width="10.5" height="10.5" />
    <rect x="1" y="12.5" width="10.5" height="10.5" />
    <rect x="12.5" y="12.5" width="10.5" height="10.5" />
  </svg>
);

const FallbackProviderIcon: React.FC<BrandIconProps> = ({ size = 20, className }) => (
  <GraduationCap size={size} className={className} aria-hidden="true" focusable="false" />
);

const providerIcons: Record<string, React.FC<BrandIconProps>> = {
  Microsoft: MicrosoftIcon,
  Google: GoogleIcon,
  Anthropic: AnthropicIcon,
  Coursera: CourseraIcon,
  Udemy: UdemyIcon,
};

/** Icon for a credential issuer; unknown providers get a generic graduation cap. */
export const getProviderIcon = (provider: string): React.FC<BrandIconProps> =>
  providerIcons[provider] ?? FallbackProviderIcon;
